/**
 * Store de gestion des grades disponibles
 * Gère la liste des grades et le grade actif
 */

import { defineStore } from 'pinia';
import type { GradeItem, GradeListConfig } from '../types';
import { configLoader } from '../services/ConfigLoader';

/**
 * État du store des grades
 */
interface GradeState {
  /** Liste des grades disponibles */
  grades: GradeItem[];
  /** Grade actif */
  activeGrade: GradeItem | null;
  /** État de chargement */
  isLoading: boolean;
  /** Erreur actuelle */
  error: string | null;
}

/**
 * Store de gestion des grades
 */
export const useGradeStore = defineStore('grade', {
  state: (): GradeState => ({
    grades: [],
    activeGrade: null,
    isLoading: false,
    error: null
  }),

  getters: {
    /**
     * Retourne un grade par son ID
     */
    gradeById: (state) => (gradeId: string): GradeItem | undefined => {
      return state.grades.find(grade => grade.item_id === gradeId);
    },

    /**
     * Retourne la liste complète des grades
     */
    gradesList: (state): GradeItem[] => state.grades
  },

  actions: {
    /**
     * Charge la liste des grades disponibles
     */
    async fetchGrades(): Promise<void> {
      this.isLoading = true;
      this.error = null;

      try {
        const gradeList: GradeListConfig = await configLoader.loadGradeList();

        // Aplatir la liste des grades depuis les groupes
        const grades: GradeItem[] = [];
        for (const group of gradeList.GRADE.groups) {
          grades.push(...group.items);
        }

        this.grades = grades;

      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Erreur lors du chargement des grades';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Définit le grade actif
     * @param gradeId ID du grade à définir comme actif
     */
    setActiveGrade(gradeId: string): void {
      const grade = this.gradeById(gradeId);
      if (grade) {
        this.activeGrade = grade;
      } else {
        this.error = `Grade avec l'ID "${gradeId}" non trouvé`;
        throw new Error(this.error);
      }
    },

    /**
     * Réinitialise le grade actif
     */
    resetActiveGrade(): void {
      this.activeGrade = null;
    },

    /**
     * Vide la liste des grades
     */
    clearGrades(): void {
      this.grades = [];
      this.activeGrade = null;
      this.error = null;
    }
  }
});